// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Button from '../Button';
import SegmentedControl from '../SegmentedControl';
import ButtonGroup from '../ButtonGroup';
import Translate from '../Translate';

import './Pagination.less';

const range = (start, length) => [...Array(length)].map((_, i) => start + i);

const getButton = value => ({ value, title: value });

const getEllipsis = key => ({ value: key, title: '...', tabIndex: -1, style: { pointerEvents: 'none' } });

const pageRange = (visible, current, total) => {
    const needEllipsis = total > visible;
    const hasStartEllipsis = needEllipsis && visible - 3 < current;
    const hasEndEllipsis = needEllipsis && current < (total - visible) + 4;

    if (!needEllipsis) {
        return range(1, total).map(getButton);
    } else if (hasStartEllipsis && !hasEndEllipsis) {
        const pageCount = visible - 2;
        return [
            getButton(1),
            getEllipsis('startEllipsis'),
            ...range(total - pageCount + 1, pageCount).map(getButton),
        ];
    } else if (!hasStartEllipsis && hasEndEllipsis) {
        const pageCount = visible - 2;
        return [
            ...range(1, pageCount).map(getButton),
            getEllipsis('endEllipsis'),
            getButton(total),
        ];
    }

    const pageCount = visible - 4;
    return [
        getButton(1),
        getEllipsis('startEllipsis'),
        ...range(current - Math.floor(pageCount / 2), pageCount).map(getButton),
        getEllipsis('endEllipsis'),
        getButton(total),
    ];
};

/**
 * `Pagination` component.
 */
const Pagination = ({
    baseClassName,
    className,
    current,
    total,
    onSelect,
    maxPageButtons,
    isShowPageButtons,
    isShowNavigationButtons,
    ...props
}) => {
    // we should hide page buttons if isShowPageButtons and isShowNavigationButtons is not defined and only two pages
    isShowPageButtons = isShowPageButtons === null ? !(isShowNavigationButtons && total < 3) : isShowPageButtons;

    return (
        <div className={classNames(baseClassName, className)} {...props}>
            {isShowPageButtons &&
                <SegmentedControl
                    className={`${baseClassName}__page-buttons`}
                    buttons={pageRange(maxPageButtons, current, total)}
                    monospaced
                    selected={current}
                    onChange={item => onSelect(item)}
                />
            }
            {isShowNavigationButtons &&
                <ButtonGroup className={`${baseClassName}__navigation-buttons`}>
                    <Button
                        disabled={current === 1}
                        onClick={() => onSelect(current - 1)}
                    >
                        <Translate content="Pagination.previous" fallback="Previous" />
                    </Button>
                    <Button
                        disabled={current === total}
                        onClick={() => onSelect(current + 1)}
                    >
                        <Translate content="Pagination.next" fallback="Next" />
                    </Button>
                </ButtonGroup>
            }
            {!isShowPageButtons &&
                <div className={`${baseClassName}__total`}>
                    <Translate
                        content="Pagination.total"
                        fallback="%%current%% of %%total%%"
                        params={{
                            current,
                            total,
                        }}
                    />
                </div>
            }
        </div>
    );
};
Pagination.propTypes = {
    /**
     * Number of pages in pagination.
     */
    total: PropTypes.number.isRequired,
    /**
     * Current page number.
     */
    current: PropTypes.number,
    /**
     * On select callback.
     */
    onSelect: PropTypes.func.isRequired,
    /**
     * Max page buttons.
     */
    maxPageButtons: PropTypes.number,
    /**
     * Set false if want to hide page buttons.
     */
    isShowPageButtons: PropTypes.bool,
    /**
     * Set false if want to hide navigation buttons.
     */
    isShowNavigationButtons: PropTypes.bool,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
Pagination.defaultProps = {
    current: 1,
    maxPageButtons: 11,
    isShowPageButtons: null,
    isShowNavigationButtons: true,
    className: null,
    baseClassName: `${CLS_PREFIX}pagination`,
};

export default Pagination;



// WEBPACK FOOTER //
// ./components/Pagination/Pagination.js